import { DisplayMessage } from '@core/app/scripts/services/display-message';
import { Eos } from '@shared/app/scripts/services/eos';
import { HandleData } from '@core/app/scripts/services/handle-data';
import { LocationManager } from '@shared/app/scripts/services/location-manager';
import { Mask } from '@core/app/scripts/services/mask';
import { SettingsManager } from '@shared/app/scripts/services/settings-manager';
import { Validation } from '@core/app/scripts/services/validation';

export class EndOfShift {
  loaded = false;

  constructor(
    private $log: ng.ILogService,
    private $q: ng.IQService,
    private displayMessage: DisplayMessage,
    private eosService: Eos,
    private handleData: HandleData,
    private legacyPrint,
    private locationManager: LocationManager,
    private mask: Mask,
    private moment,
    private paymentTypes,
    private settingsManager: SettingsManager,
    private $translate: ng.translate.ITranslateService,
    private validation,
  ) {}

  /**
   * @name posApp.endOfShift#eos
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The EOS object
   */
  eos = {};

  /**
   * @name posApp.endOfShift#device
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The device object which represents the pysical POS device
   */
  device = {};

  /**
   * @name posApp.endOfShift#cashier
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The cashier object which represents the sales person
   */
  cashier = {};

  /**
   * @name posApp.endOfShift#payments
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The object containing all of the payment metadata such as cash, pin, etc
   */
  payments = { cash: {}, electronic: {}, other: {} };

  /**
   * @name posApp.endOfShift#totals
   * @propertyOf posApp.endOfShift
   *
   * @description
   * An object containing totals for cash, electronic, and other payments
   */
  totals = { cash: 0, electronic: 0, other: 0 };

  /**
   * @name posApp.endOfShift#payGroups
   * @propertyOf posApp.endOfShift
   *
   * @description
   * More metadata metadata for groups of payments. Groups are cash, electrinic,
   * and other
   */
  payGroups = {};

  /**
   * @name posApp.endOfShift#groupers
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The query for the EOS data
   */
  groupers = {
    cash: {},
    consignment: {},
    creditcard: {},
    creditreceipt: {},
    pin: {},
    storecredit: {},
    withdrawel: {},
    deposit: {},
    change: {},
  };

  /**
   * @name posApp.endOfShift#payRules
   * @propertyOf posApp.endOfShift
   *
   * @description
   * Metadata guiding how turnover is calculated and whether or not fields are
   * visable or hidden.
   */
  payRules = {};

  /**
   * @name posApp.endOfShift#originalEOSValues
   * @propertyOf posApp.endOfShift
   *
   * @description
   * A variable for storing the original values before they are edited by a user
   */
  originalEOSValues = {};

  /**
   * @name posApp.endOfShift#coinTypes
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The metadata for the coins and bills that are available for being counted
   */
  coinTypes = [];

  /**
   * @name posApp.endOfShift#coinTotal
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The totals of all bills and coins of the cash drawer
   */
  coinTotal = 0;

  /**
   * @name posApp.endOfShift#maxDep
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The maximum deposit amount
   */
  maxDep = 0;

  /**
   * @name posApp.endOfShift#cashDepositedInPOS
   * @propertyOf posApp.endOfShift
   *
   * @description
   * Net cash added to the register. (ie: cash received, minus withdrawel and change)
   */
  cashDepositedInPOS = 0;

  /**
   * @name posApp.endOfShift#cycleId
   * @propertyOf posApp.endOfShift
   *
   * @description
   * The current shift cycle Id. This is a UUID which is used to group transactions to an EOS document
   */
  cycleId = '';

  /**
   * @name posApp.endOfShift#setShiftCycleId
   * @methodOf posApp.endOfShift
   *
   * @param {object} transaction a transaction object. can be a sale, consignment, or transit
   *
   * @description
   * Sets the shift cycle Id which identifies a transaction
   * as belonging to a certain EOs shift document.
   */
  setShiftCycleId = (transaction) => {
    if (this.settingsManager.settings.closingProcedure === 'eos') {
      transaction.shift = this.cycleId;
      this.$log.log('[EOS] Set transaction CycleId', this.cycleId);
    }
  };

  /**
   * @name posApp.endOfShift#init
   * @methodOf posApp.endOfShift
   *
   * @description
   * Makes a request to eos/init endpoint and returns a promise whose response is either
   * an existing EOS document or a new one.
   */
  init = () => {
    if (this.settingsManager.settings.closingProcedure === 'eos') {
      return this.eosService.init().then((eosInit) => {
        this.cycleId = eosInit[0].cycleID;
        this.loaded = true;

        this.$log.log('[EOS] Current shift CycleId', this.cycleId);
      });
    } else {
      this.loaded = true;
      this.$log.info(
        '[EOS] Account not configured for EOS!',
        this.settingsManager.settings.closingProcedure,
      );
      return this.$q.when();
    }
  };

  /**
   * @name posApp.endOfShift#informationalPaymentTotals
   * @methodOf posApp.endOfShift
   *
   * @description
   * Returns the ordered metadata for the payment info popups in the EOS Overview view.
   */
  informationalPaymentTotals = (paymentKey, label, payments) => {
    let pay;
    const paymentInformation = [];

    for (const paymentName in payments[paymentKey]) {
      pay = payments[paymentKey][paymentName];

      if (pay.symbol == '+' || pay.symbol == '-') {
        paymentInformation.push(pay);
      }
    }

    const orderedPaymentInformation = [
      paymentInformation[5],
      paymentInformation[0],
      paymentInformation[2],
      paymentInformation[3],
      paymentInformation[1],
      paymentInformation[4],
    ];

    return orderedPaymentInformation;
  };

  /**
   * @name posApp.endOfShift#initEosOverview
   * @methodOf posApp.endOfShift
   *
   * @description
   * Setup function for the EOS Overview view. Saves the shop, device, and cashier to
   * the EOS service.
   */
  initEosOverview = (device, cashier) => {
    this.reset();

    this.device = device;
    this.cashier = cashier;

    this.payRules = angular.copy(this.paymentTypes.rules);

    //Add other payments separetly
    this.payRules.change = {
      active: true,
      type: 'cash',
      turnover: '-',
      lineItem: true,
      readOnly: false,
    };
    this.payRules.couponin = {
      active: true,
      type: 'other',
      turnover: '+',
      lineItem: true,
      readOnly: false,
    };
    this.payRules.couponout = {
      active: true,
      type: 'other',
      turnover: '+',
      lineItem: true,
      readOnly: true,
    };
    this.payRules.storecreditin = {
      active: true,
      type: 'other',
      turnover: '+',
      lineItem: true,
      readOnly: true,
    };
    this.payRules.creditreceiptin = {
      active: true,
      type: 'other',
      turnover: '+',
      lineItem: true,
      readOnly: false,
    };
    this.payRules.cash = {
      active: true,
      type: 'cash',
      turnover: '+',
      lineItem: false,
      readOnly: false,
    };
    this.payRules.withdrawel = {
      active: true,
      type: 'cash',
      turnover: '+',
      lineItem: false,
      readOnly: false,
    };
    this.payRules.deposit = {
      active: true,
      type: 'cash',
      turnover: '+',
      lineItem: false,
      readOnly: false,
    };

    //Aggregate all payment methods
    // TODO refactor out the payRules
    for (const i in this.payRules) {
      if (!this.payments[this.payRules[i].type]) {
        this.payments[this.payRules[i].type] = {};
      }

      //only include payment options
      if (this.payRules[i].lineItem) {
        continue;
      }
    }
  };

  /**
   * @name posApp.endOfShift#indexPayments
   * @methodOf posApp.endOfShift
   *
   * @description
   * Sets up the payments metadata object
   */
  indexPayments = () => {
    for (const i in this.payGroups) {
      this.eos.final[i] = this.payGroups[i];
      if (this.payments[this.payRules[i].type]) {
        this.payments[this.payRules[i].type][i] = {
          value: this.payGroups[i],
          readOnly: this.payRules[i].readOnly,
          symbol: this.payRules[i].turnover,
        };
        if (this.payRules[i].display) {
          this.payments[this.payRules[i].type][i].label =
            this.payRules[i].display;
        } else {
          // is called with: cash change pin creditcard couponin couponout
          // creditreceiptin storecreditin deposit withdrawel storecredit
          // consignment

          this.payments[this.payRules[i].type][i].label =
            this.$translate.instant(i);
        }
      }
    }

    this.calculateTotals();
  };

  /**
   * @name posApp.endOfShift#calcMaxDep
   * @methodOf posApp.endOfShift
   *
   * @description
   * Calculates the maximum deposit using the cash and change properties. This
   * affects the endBalance
   */
  calcMaxDep = () => {
    const change = this.payGroups.change ? this.payGroups.change : 0;

    this.maxDep = this.eos.openingBalance - change;

    if (this.payGroups.cash) {
      this.maxDep += this.payGroups.cash;
    }

    if (this.payGroups.withdrawel) {
      this.maxDep += this.payGroups.withdrawel;
    }

    if (this.payGroups.deposit) {
      this.maxDep += this.payGroups.deposit;
    }

    this.maxDep = this.validation.correct(this.maxDep, 'fixed', {
      float: true,
    });

    this.eos.endBalance = this.maxDep;
  };

  /**
   * @name posApp.endOfShift#submit
   * @methodOf posApp.endOfShift
   *
   * @description
   * This function prepares the EOS for saving
   * @param {object} the EOS document
   * @param {string} the email of the user
   * @return {promise} promise
   */
  submit = (eosValue, email, printReceit = true) => {
    const completeEOS = () => {
      this.reset();
      // If there is still an open EOS, the user will need to complete it
      if (eosValue.outstandingEOS) {
        return this.$q.reject('outstandingEOSDocument');
      } else {
        return this.$q.when('finalEOSDocument');
      }
    };

    return this.save(eosValue).then((eosSaved) => {
      if (email) {
        this.eosService.email(eosSaved._id, [email]);
      }

      if (printReceit) {
        // Print the EOS transaction
        return this.legacyPrint
          .eos(
            eosSaved,
            eosSaved.device.name,
            this.locationManager.current.name,
            eosSaved.cashier.fullname,
          )
          .then(completeEOS, completeEOS);
      } else {
        completeEOS();
      }
    }, this.mask.hide);
  };

  /**
   * @name posApp.endOfShift#load
   * @methodOf posApp.endOfShift
   *
   * @description
   * This function loads the EOS object from the database during a certain periodEnd
   * and for a particular device and shift.
   * @param {string} deviceId the deviceId of the physical device
   * @return {promise} promise
   */
  load = (deviceId) => {
    const promises = [];
    let promise;

    this.displayMessage.showLoading();

    this.reset();

    return this.eosService
      .getOverview(deviceId)
      .then((eosOverview) => {
        let message, eosValue;

        //comes here through selectshift, which ran reset
        if (
          !eosOverview ||
          !eosOverview.openShifts ||
          eosOverview.openShifts.length === 0
        ) {
          return this.$q.reject(
            this.$translate.instant('ShiftAlreadyCompleted'),
          );
        } else if (
          eosOverview &&
          eosOverview.openShifts &&
          eosOverview.openShifts.length
        ) {
          //backup display because jqm prevents the first one from showing on activate.
          this.displayMessage.showLoading();

          // The user will be forced to close the oldest EOS first
          eosValue = eosOverview.openShifts[0];

          const receiptTotals = eosOverview.receiptTotals;
          const paymentTotals = eosOverview.paymentTotals;
          const endBalance = eosOverview.endBalance;

          // This flag will be used in the UI to show the user that the EOS is outstanding
          if (eosOverview.openShifts.length > 1) {
            eosValue.outstandingEOS = true;
            this.displayMessage.message({
              message: this.$translate.instant('OutstandingEOSOpen'),
              type: 'prompt',
              ok: true,
            });
          }

          eosValue.final = eosOverview.final;
          eosValue.original = eosOverview.original;

          // TODO use the data directly form pos service
          eosValue.cashier = {
            name: this.cashier.name,
            fullname: this.cashier.fullname,
            id: this.cashier._id,
          };

          // TODO use the data directly form pos service
          eosValue.device = {
            name: this.device.name,
            id: this.device.device_id,
          };

          eosValue.shop = {
            name: this.locationManager.current.name,
            id: this.locationManager.current.wh,
          };

          // The opening balance is the endBalance from the previous shift
          eosValue.openingBalance = endBalance;

          // The amount of cash that would be in the drawer if no money goes missing
          eosValue.expectedCashInDrawer = eosOverview.expectedCashInDrawer;
          eosValue.turnover = eosOverview.turnover;

          // When no cash is missing, totalCahsInDrawer is equal to expectedCashInDrawer.
          // totalCashInDrawer can be overwritten by a manual value in eos-edit.
          eosValue.totalCashInDrawer = eosOverview.expectedCashInDrawer;

          this.eos = eosValue;

          /*
           * The paymentTotals are an aggregate of the sales (type 2) and
           * the consignment (type 9) transactions. This will provide the
           * sums used to calculate the payment values.
           *
           * The receiptTotals are an aggregate of the coupons and store credit
           * payments which are aggregated from the receipt array of a sales transaction.
           */
          for (let i in this.groupers) {
            if (paymentTotals[i]) {
              this.payGroups[i] += paymentTotals[i];
            }
          }

          this.payGroups.couponin = _.defaultTo(receiptTotals.couponIn, 0);
          this.payGroups.couponout = _.defaultTo(receiptTotals.couponOut, 0);

          //creditreceipt = aggregation of creditreceipt payments (these are negative) and addition of claimed receipts (these are positive)
          this.payGroups.creditreceiptin = _.defaultTo(
            receiptTotals.creditReceipt,
            0,
          );
          this.payGroups.storecreditin = _.defaultTo(
            receiptTotals.storeCredit,
            0,
          );

          this.calculateTotals();
        }
      })
      .then(() => {
        this.calcMaxDep();

        // Don't reset missing value
        // TODO unit test
        if (angular.isDefined(this.eos.deposit)) {
          this.eos.deposit = this.validation.correct(
            this.eos.deposit,
            'fixed',
            {
              dot: true,
            },
          );
        }

        this.mask.hide();

        return this.eos;
      });
  };

  reset = () => {
    let i, g, t;

    for (i in this.coinTypes) {
      this.coinTypes[i].qty = 0;
      this.coinTypes[i].total = 0;
    }

    for (g in this.payRules) {
      this.payGroups[g] = 0;
    }
    for (t in this.payments) {
      for (i in this.payments[t]) {
        this.payments[t][i].value = 0;
      }
    }

    this.eos = {};
    this.originalEOSValues = {};

    this.totals = { cash: 0, electronic: 0, other: 0 };

    this.coinTotal = 0;
    this.cashDepositedInPOS = 0;
  };

  calculateTotals = () => {
    this.totals.cash = 0;
    this.totals.electronic = 0;
    this.totals.other = 0;

    this.totals['cash'] += this.eos.final['cash'];
    this.totals['cash'] += this.eos.final['withdrawel'];
    this.totals['cash'] -= this.eos.final['change'];
    this.totals['cash'] += this.eos.final['deposit'];
    this.totals['electronic'] += this.eos.final['creditcard'];
    this.totals['electronic'] += this.eos.final['pin'];
    this.totals['other'] += this.eos.final['creditreceipt'];
    this.totals['other'] += this.eos.final['storecredit'];
    this.totals['other'] += this.eos.final['couponin'];
    this.totals['other'] += this.eos.final['couponout'];
    this.totals['other'] += this.eos.final['storecreditin'];
    this.totals['other'] += this.eos.final['creditreceiptin'];

    if (this.payGroups.cash) {
      const change = this.payGroups.change ? this.payGroups.change : 0;
      this.cashDepositedInPOS = this.payGroups.cash - change;
    }
  };

  /**
   * @name posApp.endOfShift#prepare
   * @methodOf posApp.endOfShift
   *
   * @description
   * Function to prepare the final EOS object by setting the appropriate totals
   * @param {object} the basic EOS object
   */
  prepare = (eosValue) => {
    let coin;
    const coins = [];
    let type;
    let payment;
    let value;

    eosValue.endBalance = this.validation.correct(
      eosValue.endBalance,
      'fixed',
      {
        float: true,
      },
    );

    // Don't reset missing value
    // TODO unit test
    if (angular.isDefined(this.eos.deposit)) {
      eosValue.deposit = this.validation.correct(eosValue.deposit, 'fixed', {
        float: true,
      });
    }

    // Count the coin totals and store them
    for (coin in this.coinTypes) {
      coins.push({
        value: this.coinTypes[coin].value,
        qty: Number(this.coinTypes[coin].qty),
      });
    }
    eosValue.cashInDrawer = coins;
    eosValue.totalCashInDrawer = this.eos.totalCashInDrawer;

    return eosValue;
  };

  /**
   * @name posApp.endOfShift#save
   * @methodOf posApp.endOfShift
   *
   * @description
   * Function saves the EOS transaction object
   * @param {object} eosToSave the basic EOS object
   * @return {promise} promise
   */
  save = (eosToSave) => {
    const eosValue = {};

    eosValue.cashier = eosToSave.cashier;
    eosValue.cycleID = eosToSave.cycleID;
    eosValue.deposit = eosToSave.deposit;
    eosValue.device = eosToSave.device;
    eosValue.difference = eosToSave.difference;
    eosValue.edited = !!eosToSave.edited;
    eosValue.endBalance = eosToSave.endBalance;
    eosValue.expectedCashInDrawer = eosToSave.expectedCashInDrawer;
    eosValue.totalCashInDrawer = eosToSave.totalCashInDrawer;
    eosValue.openingBalance = eosToSave.openingBalance;
    eosValue.periodEnd = this.moment(eosToSave.periodEnd).utcOffset(0).format();
    eosValue.periodStart = this.moment(eosToSave.periodStart)
      .utcOffset(0)
      .format();
    eosValue.remarks = eosToSave.remarks;
    eosValue.shift = eosToSave.shift;
    eosValue.shop = eosToSave.shop;
    eosValue.turnover = eosToSave.turnover;
    eosValue.original = eosToSave.original;
    eosValue.final = eosToSave.edited ? eosToSave.final : eosToSave.original;

    eosValue._id = eosToSave._id;

    this.prepare(eosValue);

    eosValue.status = 'completed';

    // TODO add some sort of validation before saving

    // Save the EOS
    return this.handleData
      .saveData(
        {
          resource: 'eos',
        },
        eosValue,
      )
      .then(() => eosValue);
  };
}

/**
 * @ngdoc service
 * @name posApp.endOfShift
 * @description
 * # End Of Shift
 * A service for the End of Shift (EOS) resource. This handles the business logic
 * of creating and loading end of shift objects. An end of shift is the end point
 * a period of sales transactions on the POS.
 */
angular
  .module('posApp')
  .factory(
    'endOfShift',
    (
      $log: ng.ILogService,
      $q: ng.IQService,
      displayMessage: DisplayMessage,
      eos: Eos,
      handleData: HandleData,
      legacyPrint,
      locationManager: LocationManager,
      mask: Mask,
      moment,
      paymentTypes,
      settingsManager: SettingsManager,
      translate: ng.translate.ITranslateService,
      validation: Validation,
    ) => {
      return new EndOfShift(
        $log,
        $q,
        displayMessage,
        eos,
        handleData,
        legacyPrint,
        locationManager,
        mask,
        moment,
        paymentTypes,
        settingsManager,
        translate,
        validation,
      );
    },
  );
